import { type Doc } from "building-types/doc";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { HomeSettingsRW } from "building-types/settings/homeSettings";
import { type SettingsSet } from "building-types/settings/settingsSet";
import type { RWTable, Table } from "common/data/table";
import { optionSetDefaultOptionId } from "doc/options/optionSetBaseUtils";
import { evalFormulas } from "../formula/evalFormulas";
import { mergeSettingsSet } from "../mergeSettingsSet";
import { entityIsGlobalSettingsSet } from "../settingsTools";

export interface FlattenSettingsSetOptions {
  settings: Doc["settings"];
  settingsSets: Doc["settingsSets"];
  entityDisabledByGroup?: Table<boolean>;
  optionChoices: OptionChoices;
  optionSets?: Doc["optionSets"];
}

export function flattenSettingsSets(mergedSettings: HomeSettingsRW, props: FlattenSettingsSetOptions) {
  const { settings, settingsSets, optionChoices, optionSets = {}, entityDisabledByGroup } = props;

  const evaluatedSettingsSets: Table<SettingsSet> = evalFormulas(
    settingsSets,
    (id) => optionChoices[id] ?? (optionSets[id] ? optionSetDefaultOptionId(optionSets[id], optionSets) : undefined),
    undefined,
    entityDisabledByGroup,
  );

  const sortedSets = Object.values(evaluatedSettingsSets)
    .filter((x) => !x.disabled)
    .sort((a, b) => a.sortIndex - b.sortIndex);

  const activeSettingsSets: RWTable<SettingsSet> = {};
  for (const settingsSet of sortedSets) {
    // Targeted Settings sets will be merged at the entity instance
    if (!settingsSet.type || entityIsGlobalSettingsSet(settingsSet)) {
      mergeSettingsSet(mergedSettings, settingsSet.id, settings);
    }

    activeSettingsSets[settingsSet.id] = settingsSet;
  }

  return { mergedSettings, activeSettingsSets };
}
